const DEFAULT_FLOW_COLOR = 'grey';
const DEFAULT_LOG_COLOR = '#6b7280';
const DEFAULT_ICON = 'mdi-circle';
const DEFAULT_LOG_GRADIENT = 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';

export const JSON_CONDITION_OPERATOR_TOKENS = Object.freeze({
	equals: '=',
	not_equals: '≠',
	greater_than: '>',
	less_than: '<',
	greater_equal: '≥',
	less_equal: '≤',
	contains: '⊃',
	not_contains: '⊅',
	starts_with: 'starts',
	ends_with: 'ends',
	regex: '~ regex',
	is_empty: 'empty',
	is_not_empty: '≠ empty',
	exists: '∃',
	not_exists: '∄',
	array_empty: '[] empty',
	array_not_empty: '[] ≠ empty',
	array_contains: '[] ⊃',
	array_length_min: 'len ≥',
	array_length_max: 'len ≤',
	array_length_equals: 'len =',
	array_all_unique: 'all unique',
	is_true: 'true',
	is_false: 'false',
	type_is: 'type:',
	one_of: '∈',
	not_one_of: '∉',
	format: 'fmt:',
	equals_field: '= field',
	greater_than_field: '> field',
	less_than_field: '< field',
});

// Entry ที่ label ทั้งสามช่องและ icon ทั้งสองช่องเป็นค่าเดียวกัน
function uniformNodeInfo(label, icon, flowColor, logColor, logGradient) {
	return Object.freeze({
		flowLabel: label,
		logLabel: label,
		workflowLabel: label,
		flowIcon: icon,
		logIcon: icon,
		flowColor,
		logColor,
		logGradient,
	});
}

// Entry ที่ใช้ฝั่ง flow เท่านั้น (ไม่มีข้อมูลฝั่ง log)
function flowOnlyNodeInfo(label, icon, flowColor) {
	return Object.freeze({
		flowLabel: label,
		workflowLabel: label,
		flowIcon: icon,
		flowColor,
	});
}

// Entry ที่ใช้ฝั่ง log เท่านั้น
function logOnlyNodeInfo(label, icon, logColor, logGradient) {
	return Object.freeze({
		logLabel: label,
		logIcon: icon,
		logColor,
		logGradient,
	});
}

export const NODE_TYPE_INFO = Object.freeze({
	messageInput: Object.freeze({
		flowLabel: 'Message Input',
		logLabel: 'Message Input',
		workflowLabel: 'Message Input',
		flowIcon: 'mdi-chat',
		logIcon: 'mdi-message-text',
		flowColor: 'green',
		logColor: '#22c55e',
	}),
	webhookTrigger: uniformNodeInfo('Webhook Trigger', 'mdi-webhook', 'purple', '#a855f7', 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)'),
	model: uniformNodeInfo('AI Model', 'mdi-robot', 'blue', '#3b82f6', 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'),
	aiModel: Object.freeze({
		logLabel: 'AI Model Processing',
		workflowLabel: 'AI Model',
		logIcon: 'mdi-robot',
		logColor: '#3b82f6',
	}),
	guardrailAI: flowOnlyNodeInfo('Guardrail AI', 'mdi-shield-check', 'green'),
	guardrail: uniformNodeInfo('Guard Rail', 'mdi-shield-check', 'green', '#22c55e', 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'),
	intentAI: flowOnlyNodeInfo('Intent AI Model', 'mdi-target-variant', 'purple'),
	intentClassifier: flowOnlyNodeInfo('Intent Classifier', 'mdi-sort-variant', 'purple'),
	intent: Object.freeze({
		flowLabel: 'Intent',
		logLabel: 'Intent Router',
		workflowLabel: 'Intent',
		flowIcon: 'mdi-crosshairs-question',
		logIcon: 'mdi-router',
		flowColor: 'purple',
		logColor: '#8b5cf6',
		logGradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
	}),
	smartIntent: uniformNodeInfo('Smart Intent', 'mdi-robot-confused', 'purple', '#4f46e5', 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)'),
	advancedRouter: uniformNodeInfo('Advanced Router', 'mdi-source-branch', 'purple', '#8b5cf6', 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'),
	jsonCondition: Object.freeze({
		label: 'JSON Condition',
		icon: 'mdi-code-json',
		color: 'indigo',
		flowLabel: 'JSON Condition',
		logLabel: 'JSON Condition',
		workflowLabel: 'JSON Condition',
		flowIcon: 'mdi-code-json',
		logIcon: 'mdi-code-json',
		flowColor: 'teal',
		logColor: '#06b6d4',
		logGradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
	}),
	apiTrigger: uniformNodeInfo('API Trigger', 'mdi-rocket-launch', 'blue', '#0ea5e9', 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'),
	orchestrator: uniformNodeInfo('Orchestrator', 'mdi-hub', 'pink', '#ec4899', 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)'),
	agent: uniformNodeInfo('Agent', 'mdi-robot', 'teal', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	tool: uniformNodeInfo('Tool', 'mdi-tools', 'orange', '#f97316', 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'),
	'agent-tool': uniformNodeInfo('Agent Tool', 'mdi-tools', 'orange', '#f97316', 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'),
	'agent-find-tools': uniformNodeInfo('Agent (Find tools)', 'mdi-robot', 'teal', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	'agent-summary': uniformNodeInfo('Agent (Summary)', 'mdi-robot', 'teal', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	'agent-validation': uniformNodeInfo('Agent (Validation)', 'mdi-robot', 'teal', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	flowTool: uniformNodeInfo('Flow Tool', 'mdi-sitemap', 'indigo', '#06b6d4', 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'),
	flowInput: uniformNodeInfo('Flow Input', 'mdi-import', 'green', '#22c55e', 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'),
	flowOutput: uniformNodeInfo('Flow Output', 'mdi-export', 'red', '#ef4444', 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'),
	contactAgent: uniformNodeInfo('Contact Agent', 'mdi-account-supervisor', 'red', '#dc2626', 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'),
	switch: flowOnlyNodeInfo('Switch', 'mdi-arrow-decision', 'red'),
	condition: flowOnlyNodeInfo('Condition', 'mdi-swap-horizontal-bold', 'green'),
	delay: flowOnlyNodeInfo('Delay', 'mdi-timer', 'blue'),
	documents: uniformNodeInfo('Documents', 'mdi-file-document-multiple', 'blue', '#0369a1', 'linear-gradient(135deg, #0369a1 0%, #075985 100%)'),
	api: flowOnlyNodeInfo('API Call', 'mdi-api', 'amber'),
	response: Object.freeze({
		flowLabel: 'Response',
		logLabel: 'Message Output',
		workflowLabel: 'Response',
		flowIcon: 'mdi-message-reply-text',
		logIcon: 'mdi-message-reply-text',
		flowColor: 'red',
		logColor: '#f97316',
	}),
	databaseIntegration: Object.freeze({
		flowLabel: 'Database Integration',
		logLabel: 'DB Integration',
		workflowLabel: 'Database Integration',
		flowIcon: 'mdi-database-search',
		logIcon: 'mdi-database-search',
		flowColor: 'green',
		logColor: '#16a34a',
		logGradient: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
	}),
	apiknowledge: Object.freeze({
		flowLabel: 'Knowledge API',
		logLabel: 'API Knowledge',
		workflowLabel: 'Knowledge API',
		flowIcon: 'mdi-cloud',
		logIcon: 'mdi-rocket-launch',
		flowColor: 'indigo',
		logColor: '#ff6c37',
		logGradient: 'linear-gradient(135deg, #ff6c37 0%, #ff5722 100%)',
	}),
	products: flowOnlyNodeInfo('Products', 'mdi-package-variant', 'amber'),
	webhook: flowOnlyNodeInfo('Webhook', 'mdi-webhook', 'green'),
	email: flowOnlyNodeInfo('Email', 'mdi-email', 'red'),
	sharepoint: flowOnlyNodeInfo('SharePoint', 'mdi-microsoft-sharepoint', 'green'),
	// S15-P1 — knowledge-source plugin ใต้ AI Model (สี/ไอคอนตาม ajt) · label ที่นี่เป็น registry ภาษาเดียว
	// ข้อความบนจอ (palette/subtitle) ใช้ i18n flowpg_nodeTypeKnowledgeGraph / flowpg_nodeTypeWaterfallSearch
	knowledgeGraph: uniformNodeInfo('Knowledge Graph', 'mdi-graph-outline', 'purple', '#7c3aed', 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'),
	nexaWaterfallSearch: uniformNodeInfo('Waterfall Search', 'mdi-stairs', 'teal', '#0f766e', 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)'),
	merge: Object.freeze({
		flowLabel: 'Merge',
		logLabel: 'Merge Results',
		workflowLabel: 'Merge',
		flowIcon: 'mdi-call-merge',
		logIcon: 'mdi-merge',
		flowColor: 'blue',
		logColor: '#06b6d4',
		logGradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
	}),
	interestOptimizer: logOnlyNodeInfo('Interest Optimizer', 'mdi-calculator', '#f59e0b', 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'),
	loanInterest: logOnlyNodeInfo('Loan Interest', 'mdi-home-analytics', '#10b981', 'linear-gradient(135deg, #10b981 0%, #059669 100%)'),
	embedding: logOnlyNodeInfo('Vector Embedding', 'mdi-code-array', '#14b8a6', 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'),
	queryRewriter: Object.freeze({
		flowLabel: 'Query Rewriter',
		flowIcon: 'mdi-message-text-clock-outline',
		flowColor: 'deep-purple',
		logLabel: 'Query Rewriter',
		logIcon: 'mdi-message-text-clock-outline',
		logColor: '#7c3aed',
		logGradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
	}),
});

function getNodeTypeInfo(nodeType) {
	return NODE_TYPE_INFO[nodeType] || {};
}

export function getJsonConditionOperatorToken(operatorType) {
	return JSON_CONDITION_OPERATOR_TOKENS[operatorType] || '';
}

export function getFlowNodeTypeColor(nodeType) {
	return getNodeTypeInfo(nodeType).flowColor || DEFAULT_FLOW_COLOR;
}

export function getFlowNodeTypeIcon(nodeType) {
	const info = getNodeTypeInfo(nodeType);
	return info.flowIcon || info.logIcon || DEFAULT_ICON;
}

export function getFlowNodeTypeLabel(nodeType) {
	const info = getNodeTypeInfo(nodeType);
	return info.flowLabel || info.workflowLabel || info.logLabel || nodeType;
}

export function getLogNodeName(nodeType) {
	const info = getNodeTypeInfo(nodeType);
	return info.logLabel || info.flowLabel || info.workflowLabel || nodeType;
}

export function getLogNodeIcon(nodeType) {
	const info = getNodeTypeInfo(nodeType);
	return info.logIcon || info.flowIcon || DEFAULT_ICON;
}

export function getLogNodeTypeColor(nodeType) {
	return getNodeTypeInfo(nodeType).logColor || DEFAULT_LOG_COLOR;
}

export function getLogNodeGradient(nodeType) {
	return getNodeTypeInfo(nodeType).logGradient || DEFAULT_LOG_GRADIENT;
}

export function getChatWorkflowLabel(nodeType) {
	if (!nodeType) {
		return '-';
	}

	const info = getNodeTypeInfo(nodeType);
	return info.workflowLabel || info.flowLabel || info.logLabel || nodeType;
}

// ─────────────────────────────────────────────────────────────────────────────
// S15-P1 — ค่าเริ่มต้น / normalize ของ node `knowledgeGraph` และ `nexaWaterfallSearch`
// ใช้ร่วมกันโดย NodePanel.vue (default data ตอนลาก) และ Flow.vue (inspector · extract/expand)
// สัญญา field ดู specs/S15-P1-GraphRAG-FlowNodeAjtParity.md §Node data contract
//   - kgRetrievalConfig = PascalCase (ตาม ajt) → backend map เข้า GraphRagModelRetrievalConfig (Newtonsoft case-insensitive)
//   - graphTiers[]      = camelCase  (ตาม ajt) → backend map เข้า WaterfallLayerConfig
// ค่าเป็น graphKey (ไม่ใช่ hex id) ตามมติ D3
// ─────────────────────────────────────────────────────────────────────────────

/**
 * กติกาการอ้างอิงมาตรฐาน — **ต้องเท่ากับ** `GraphRagChatKnowledgeBal.CONST_BASE_CITATION_RULE`
 * (BAL/GraphRAG/Query/GraphRagChatKnowledgeBal.cs) ทุกตัวอักษร · TC-S15P1-52 ตรวจความเท่ากัน
 * ถ้าฝั่ง C# เปลี่ยนข้อความ ต้องเปลี่ยนที่นี่ด้วย (โมเดลถูกจูนกับข้อความนี้แล้ว)
 */
export const KG_BASE_CITATION_RULE =
	'When answering with information from the Citation Sources above, cite the relevant factual sentences with the exact [n] markers provided. Do not invent source numbers. Preserve citation markers in the final answer.';

/**
 * โหมดค้นหาที่ backend รองรับ — ต้องตรงกับ `GraphRagDefaults.SearchModes`
 * (BAL/GraphRAG/Core/Defaults/GraphRagDefaults.cs) และชุดเดียวกับ Model Creator (ModelManagement.vue KG_SEARCH_MODES)
 * โหมดนอกรายการนี้จะถูก `GraphRagUserSettings.Validate` ปฏิเสธเป็นราย graph
 */
export const KG_SEARCH_MODES = Object.freeze(['fuzzy-first', 'semantic-first', 'bm25-node-name', 'hybrid-union', 'hybrid-rerank', 'baseline-vector-only']);

// อ่านค่าแรกที่ไม่ใช่ undefined/null จากชื่อ key ที่ให้ (รับ PascalCase + camelCase · ไม่รับ snake_case — spec §Node data contract)
function pickField(raw, ...keys) {
	for (const k of keys) {
		if (raw[k] !== undefined && raw[k] !== null) {
			return raw[k];
		}
	}
	return undefined;
}

// ตัวเลขหรือค่า default — v-text-field type="number" คืน string จึงต้อง Number() เสมอ
function numOrDefault(val, def) {
	if (val === undefined || val === null || val === '') {
		return def;
	}
	const n = Number(val);
	return Number.isNaN(n) ? def : n;
}

// ตัวเลขหรือ null (ช่องที่ null = ไม่ override ใช้ค่าของกราฟ)
function nullableNum(val) {
	if (val === undefined || val === null || val === '') {
		return null;
	}
	const n = Number(val);
	return Number.isNaN(n) ? null : n;
}

// bool — undefined = default · ค่าอื่นเป็น true ยกเว้น false ชัด ๆ (ตาม ajt)
function boolOrDefault(val, def) {
	return val === undefined ? def : val !== false;
}

/**
 * ค่าเริ่มต้น `kgRetrievalConfig` ของ node `knowledgeGraph` (PascalCase ตาม ajt)
 * ค่าตรงกับ Model Creator (ModelManagement.vue KG_RETRIEVAL_DEFAULTS) + `CitationPrompt` = กติกามาตรฐาน
 */
export function defaultKgRetrievalConfig() {
	return {
		MaxHops: 3,
		SearchMode: 'fuzzy-first',
		FuzzyThreshold: 70,
		SemanticThreshold: 0.65,
		MaxContextNodes: 30,
		MaxContextEdges: 60,
		BaselineTopK: null,
		BaselineMinSimilarity: null,
		UseCitationContext: true,
		CitationPrompt: KG_BASE_CITATION_RULE,
	};
}

/**
 * ทำ `kgRetrievalConfig` ที่อ่านจาก flow JSON ให้ครบทุก field เป็น PascalCase
 * รับทั้ง PascalCase (ที่ save) และ camelCase (เผื่อ JSON ที่ import มาจากที่อื่น) · ค่าว่าง/ไม่ใช่ตัวเลข = default
 */
export function normalizeKgRetrievalConfig(raw) {
	const d = defaultKgRetrievalConfig();
	if (!raw || typeof raw !== 'object') {
		return d;
	}
	return {
		MaxHops: numOrDefault(pickField(raw, 'MaxHops', 'maxHops'), d.MaxHops),
		SearchMode: pickField(raw, 'SearchMode', 'searchMode') || d.SearchMode,
		FuzzyThreshold: numOrDefault(pickField(raw, 'FuzzyThreshold', 'fuzzyThreshold'), d.FuzzyThreshold),
		SemanticThreshold: numOrDefault(pickField(raw, 'SemanticThreshold', 'semanticThreshold'), d.SemanticThreshold),
		MaxContextNodes: numOrDefault(pickField(raw, 'MaxContextNodes', 'maxContextNodes'), d.MaxContextNodes),
		MaxContextEdges: numOrDefault(pickField(raw, 'MaxContextEdges', 'maxContextEdges'), d.MaxContextEdges),
		BaselineTopK: nullableNum(pickField(raw, 'BaselineTopK', 'baselineTopK')),
		BaselineMinSimilarity: nullableNum(pickField(raw, 'BaselineMinSimilarity', 'baselineMinSimilarity')),
		UseCitationContext: boolOrDefault(pickField(raw, 'UseCitationContext', 'useCitationContext'), d.UseCitationContext),
		// ตัดช่องแก้ Citation Prompt ออกจากโหนดแล้ว (2026-10-20) ⇒ ใช้กติกามาตรฐานเสมอ
		// ค่าที่เคยแก้ไว้ใน flow เก่าถูกทับด้วยค่ามาตรฐานตอนบันทึกครั้งถัดไป (ไม่ปล่อยให้ค้างแบบมองไม่เห็น)
		CitationPrompt: d.CitationPrompt,
	};
}

/**
 * tier ใหม่ 1 ชั้นของ node `nexaWaterfallSearch` (camelCase ตาม ajt) · `graphId` = graphKey ที่ผู้ใช้เลือก (null = ยังไม่เลือก)
 */
export function defaultWaterfallTier() {
	return {
		graphId: null,
		searchMode: 'fuzzy-first',
		maxHops: 3,
		citationEnabled: true,
		fuzzyThreshold: 70,
		semanticThreshold: 0.65,
		maxContextNodes: 30,
		maxContextEdges: 60,
		anchorFinalTopK: 10,
		baselineTopK: null,
		baselineMinSimilarity: null,
	};
}

/**
 * ทำ tier ที่อ่านจาก flow JSON ให้ครบทุก field เป็น camelCase
 * รับทั้ง camelCase (ที่ save) และ PascalCase · ตัวเลขถูกแปลงด้วย Number() (ต่างจาก ajt ที่ใช้ ?? เฉย ๆ — กัน string จาก v-text-field)
 */
export function normalizeWaterfallTier(raw) {
	const d = defaultWaterfallTier();
	if (!raw || typeof raw !== 'object') {
		return d;
	}
	return {
		graphId: pickField(raw, 'graphId', 'GraphId') ?? d.graphId,
		searchMode: pickField(raw, 'searchMode', 'SearchMode') || d.searchMode,
		maxHops: numOrDefault(pickField(raw, 'maxHops', 'MaxHops'), d.maxHops),
		citationEnabled: boolOrDefault(pickField(raw, 'citationEnabled', 'CitationEnabled'), d.citationEnabled),
		fuzzyThreshold: numOrDefault(pickField(raw, 'fuzzyThreshold', 'FuzzyThreshold'), d.fuzzyThreshold),
		semanticThreshold: numOrDefault(pickField(raw, 'semanticThreshold', 'SemanticThreshold'), d.semanticThreshold),
		maxContextNodes: numOrDefault(pickField(raw, 'maxContextNodes', 'MaxContextNodes'), d.maxContextNodes),
		maxContextEdges: numOrDefault(pickField(raw, 'maxContextEdges', 'MaxContextEdges'), d.maxContextEdges),
		anchorFinalTopK: numOrDefault(pickField(raw, 'anchorFinalTopK', 'AnchorFinalTopK'), d.anchorFinalTopK),
		baselineTopK: nullableNum(pickField(raw, 'baselineTopK', 'BaselineTopK')),
		baselineMinSimilarity: nullableNum(pickField(raw, 'baselineMinSimilarity', 'BaselineMinSimilarity')),
	};
}
